<template>
    <div class="modal-container">
        <div class="modal-header">
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <simple-wizard
                plain
                :show-header="false"
                :title="title + mainTitle"
                sub-title=""
                @finish="validateDetails"
                @stepChange="stepChange"
                :finish-button-text="title"
                next-button-text="next"
            >
                <wizard-tab :before-change="validateDetails" :id="'adjustment'">
                    <template slot="label">
                        Details
                    </template>

                    <form-wrapper :validator="$v.adjustment" class="form-wrapper">
                        <adjustment-step
                            v-if="title.toLowerCase().trim() === 'create'"
                            :adjustment-details="adjustment"
                            @isDriver="setDriver"
                            @isCustomer="setCustomer"
                            :show-customer="isCustomer"
                            :show-driver="isDriver"
                        />

                        <adjustment-details-step
                            :adjustment-details="adjustment"
                            :categories="invoiceAdjustmentCategories"
                            :team-members="teamMembers"
                            :customers="customerList"
                        />
                    </form-wrapper>
                </wizard-tab>
            </simple-wizard>
            <div>
                <b>Important:</b> When this adjustment is approved, it will automatically be added to any draft invoices that already exist for the same date range.
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { required, numeric, requiredIf } from 'vuelidate/lib/validators';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { SimpleWizard, WizardTab } from '@/components';
import { handleRequests, showErrorMessage } from '@/helpers';
import AdjustmentDetailsStep from './steps/AdjustmentDetailsStep';
import AdjustmentStep from './steps/AdjustmentStep';

export default {
    name: 'CreateOrUpdateInvoiceAdjustmentModal',
    components: {
        SimpleWizard,
        WizardTab,
        AdjustmentStep,
        AdjustmentDetailsStep
    },
    mixins: [GeneralMixin],
    props: {
        resolve: {
            type: Function,
            default: () => {}
        },
        invoiceAdjustmentCategories: {
            type: Array,
            default: () => []
        },
        invoiceAdjustmentStatuses: {
            type: Array,
            default: () => []
        },
        teamMembers: {
            type: Array,
            default: () => []
        },
        customerList: {
            type: Array,
            default: () => []
        },
        adjustmentData: {
            type: Object,
            default: () => {}
        },
        title: {
            type: String,
            default: 'Create'
        },
        startTab: {
            type: Number,
            default: 0
        },
        isDriver: {
            type: Boolean,
            default: false
        },
        isCustomer: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            adjustment: {
                customerIds: [],
                teamMemberUserIds: [],
                shouldApplyTax: false,
                tripDate: null,
                description: null,
                amount: null,
                accountCode: null,
                accountCodeDescription: null,
                invoiceAdjustmentCategory: null,
                invoiceAdjustmentStatusId: null,
                isCustomer: false,
                isDriver: false,
                isEditing: false,
                invoiceAdjustmentId: null,
                percentageAmount: null,
                adjustmentIsPercentage: false
            },
            step: 'adjustmentParentDetails',
            isCreatingOrUpdating: false,
            mainTitle: 'Adjustment'
        };
    },
    validations: {
        adjustment: {
            amount: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return !this.adjustment.adjustmentIsPercentage;
                    })
            },
            invoiceAdjustmentCategory: {
                invoiceAdjustmentCategoryId: {
                    required,
                    numeric
                }
            },
            description: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.adjustment.invoiceAdjustmentCategory?.category === 'Other';
                    })
            },
            teamMemberUserIds: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.adjustment.isDriver && !this.adjustment.isEditing;
                    })
            },
            customerIds: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.adjustment.isCustomer && !this.adjustment.isEditing;
                    })
            },
            percentageAmount: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.adjustment.adjustmentIsPercentage;
                    })
            }
        },
        adjustmentParentDetails: {
            isCustomer: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return (
                            (this.adjustment.isCustomer === false && this.adjustment.isDriver === false) ||
                            (this.adjustment.isCustomer === undefined && this.adjustment.isDriver === undefined)
                        );
                    })
            },
            isDriver: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return (
                            (this.adjustment.isCustomer === false && this.adjustment.isDriver === false) ||
                            (this.adjustment.isCustomer === undefined && this.adjustment.isDriver === undefined)
                        );
                    })
            }
        }
    },
    async mounted() {
        this.$v.$reset();
        this.adjustment = {
            customerIds: [],
            teamMemberUserIds: [],
            shouldApplyTax: false,
            tripDate: null,
            description: null,
            amount: null,
            accountCode: null,
            accountCodeDescription: null,
            invoiceAdjustmentCategory: null,
            invoiceAdjustmentStatusId: null,
            isCustomer: false,
            isDriver: false,
            isEditing: false,
            invoiceAdjustmentId: null,
            percentageAmount: null,
            adjustmentIsPercentage: false
        };

        this.stepChange('adjustment');

        if (this.adjustmentData) {
            this.adjustment = {
                ...this.adjustment,
                ...this.adjustmentData
            };

            this.adjustment.isCustomer = this.adjustment.customerId !== null;
            this.adjustment.isDriver = this.adjustment.teamMemberId !== null;

            this.adjustment.isEditing = true;

            if (this.adjustment.tripDate) {
                this.adjustment.tripDate = moment(this.adjustment.tripDate).format('YYYY-MM-DD');
            }

            this.adjustment.adjustmentIsPercentage =
                this.adjustment.percentageAmount !== null && this.adjustment.percentageAmount !== undefined;
        } else {
            const pendingStatus = this.invoiceAdjustmentStatuses.find((x) => x.status === 'Awaiting approval');
            this.adjustment.invoiceAdjustmentStatusId = pendingStatus.invoiceAdjustmentStatusId;
        }
    },
    methods: {
        validateDetails() {
            if (!this.adjustment.isDriver && !this.adjustment.isCustomer) {
                this.$notify({
                    message: 'Please select either a Team Member or Customer.',
                    type: 'danger'
                });
                return false;
            }

            this.$v[this.step].$touch();
            if (!this.$v[this.step].$invalid) {
                if (this.step === 'adjustment') {
                    if (this.title.toLowerCase().trim() === 'update') {
                        this.updateExisting();
                    } else {
                        this.create();
                    }
                }

                return true;
            }

            return false;
        },
        stepChange(stepName) {
            this.step = stepName;
        },
        setDriver(isDriver) {
            this.adjustment.isDriver = isDriver;
        },
        setCustomer(isCustomer) {
            this.adjustment.isCustomer = isCustomer;
        },
        async create() {
            if (this.isCreatingOrUpdating) 
                return;

            this.isCreatingOrUpdating = true;
            this.$_handleLoaderState(true, 'CREATING...');

            if (this.adjustment.tripDate) {
                this.adjustment.tripDate = this.adjustment
                    ? moment(this.adjustment.tripDate).format('YYYY-MM-DD')
                    : null;
            }

            let response;

            const payload = {
                method: 'post',
                data: this.adjustment
            };

            const api = `/api/invoice-adjustments/create`;
            try {
                response = await handleRequests(api, payload);
            } catch (e) {
                const message = `Error when creating new adjustment.`;
                showErrorMessage(this, message, null);
            } finally {
                this.$v.$reset();
                this.$_handleLoaderState(false);
                this.isCreatingOrUpdating = false;
                this.resolve(response.data);
            }
        },
        async updateExisting() {
            if (this.isCreatingOrUpdating)
                return;

            this.isCreatingOrUpdating = true;
            this.$_handleLoaderState(true, 'UPDATING...');

            const payload = {
                method: 'put',
                data: this.adjustment
            };

            const api = `/api/invoice-adjustments/${this.adjustment.invoiceAdjustmentId}`;
            try {
                await handleRequests(api, payload);
            } catch (e) {
                const message = `Error when updating adjustment.`;
                showErrorMessage(this, message, null);
            } finally {
                this.$v.$reset();
                this.$_handleLoaderState(false);
                this.resolve(this.adjustment);
                this.isCreatingOrUpdating = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.modal-container {
    max-width: 800px;

    .modal-header {
        padding: 0;
        button {
            z-index: 99;
        }
    }
    .modal-body {
        padding-top: 0;
        max-height: 80vh;
        overflow-y: auto;
        overflow-x: hidden;
        h5 {
            font-weight: 400;
            text-transform: uppercase;
            font-size: 15px;
        }

        ::v-deep .md-card {
            margin: 0 !important;
            background-color: #fff;
            box-shadow: none;

            .md-card-content {
                overflow-y: visible;
                padding: 20px 0 0 0;
                // max-height: 435px;

                .md-size-50:first-child {
                    padding-left: 0;
                    padding-right: 30px;
                }

                .md-size-50:last-child {
                    padding-left: 30px;
                    padding-right: 0;
                }
            }
        }

        ::v-deep .tab-content {
            padding: 0 15px 0 15px !important;
        }
        .md-icon-button,
        ::v-deep .md-icon-button {
            background-color: #ffffff !important;
        }
    }
}
</style>
